/**
 * The interviews list cannot ask for its data until the app shell has loaded
 * the user, permissions and saved filters — on staging that is ~2.7s in, and
 * the request itself takes another ~1s. Remembering the last first-page request
 * lets the app fire it during boot instead (`app/preloadInterviewsListRoute`),
 * so it is usually answered by the time the list mounts.
 *
 * Only a hint: the list still builds its own params, and reuses the boot
 * request only when they serialise identically (see `consumeBootPrefetch`).
 */
const STORAGE_KEY = 'kula_interviews_list_last_request';

// "Now", to the minute — the list's time boundary. Kept in one place so the
// replayed request and the list compute the same cache key.
export const currentMinuteIso = (): string =>
  new Date(Math.floor(Date.now() / 60_000) * 60_000).toISOString();

type ListParams = {
  filter_by?: Record<string, unknown>;
  page?: number;
  scroll_token?: unknown;
  [key: string]: unknown;
};

// One entry per tab: with a single slot, landing on the tab you did not use
// last always missed the boot replay. `{ tab, params }` is the older
// single-slot shape, still read so an existing hint is not lost.
type SavedRequests = Record<string, ListParams>;
type LegacySavedRequest = { tab: string; params: ListParams };

const readSaved = (): SavedRequests => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as SavedRequests | LegacySavedRequest;
    if (
      parsed &&
      typeof (parsed as LegacySavedRequest).tab === 'string' &&
      (parsed as LegacySavedRequest).params
    ) {
      const legacy = parsed as LegacySavedRequest;
      return { [legacy.tab]: legacy.params };
    }
    return (parsed as SavedRequests) ?? {};
  } catch {
    return {};
  }
};

/** Records a list request; only first pages, the ones a fresh visit starts on. */
export const saveLastListRequest = (tab: string, params: ListParams): void => {
  if (params.page !== 1 || params.scroll_token) return;
  try {
    // JSON drops the undefined keys, exactly as RTK Query's serialiser does.
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readSaved(), [tab]: params })
    );
  } catch {
    // Storage full or blocked: the list simply loads the usual way.
  }
};

/**
 * The last first-page request for `tab`, with its time boundary moved to the
 * current minute, or null when there is none to replay.
 */
export const readLastListRequest = (tab: string): ListParams | null => {
  const saved = readSaved()[tab];
  if (!saved?.filter_by) return null;

  const filterBy = { ...saved.filter_by };
  const now = currentMinuteIso();
  if ('ends_before' in filterBy) filterBy.ends_before = now;
  if ('ends_after' in filterBy) filterBy.ends_after = now;
  return { ...saved, filter_by: filterBy };
};

// Key-order independent, and drops undefined like RTK Query's serialiser.
export const argsKey = (value: unknown): string =>
  JSON.stringify(value, (_, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(
          Object.keys(v)
            .sort()
            .map((k) => [k, v[k]])
        )
      : v
  );

let bootArgsKey: string | null = null;

/** Called by the boot preload with the params it requested. */
export const markBootPrefetch = (params: unknown): void => {
  bootArgsKey = argsKey(params);
};

/**
 * True exactly once, for params matching the boot request — the list then reads
 * that (possibly still in-flight) entry instead of firing a duplicate. Later
 * fetches, and any visit after navigating away, always go to the network.
 */
export const consumeBootPrefetch = (params: unknown): boolean => {
  if (bootArgsKey === null) return false;
  const matches = bootArgsKey === argsKey(params);
  bootArgsKey = null;
  return matches;
};
